/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable, throwError } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { StationDto } from '../models';
import { GeometryType } from '../models/geometry-type';
import { Point } from '../models/point';
import { PriceDto } from '../models/price-dto';
import { parseAddress } from '../../utils/address.utils';
import { environment } from '../../../environments/environment';
import { FuelPrice, Station } from './interfaces/station-response';

@Injectable({
  providedIn: 'root',
})
export class StationPricesService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation stationPricesGetStationsFromPoint
   */
  static readonly StationPricesGetStationsFromPointPath = '/api/nsw/fuel/stations-nearby';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `stationPricesGetStationsFromPoint()` instead.
   *
   * This method doesn't expect any request body.
   */
  stationPricesGetStationsFromPoint$Response(params?: {
    pointLng?: number;
    pointLat?: number;
    distanceInKm?: number;
    fuelType?: string;
  }): Observable<StrictHttpResponse<Array<StationDto>>> {
    const request = new RequestBuilder(
      environment.nswFuelApiUrl,
      StationPricesService.StationPricesGetStationsFromPointPath,
      'get'
    );
    if (params) {
      request.query('lng', params.pointLng, {});
      request.query('lat', params.pointLat, {});
      request.query('radius', params.distanceInKm, {});
      request.query('fuelType', params.fuelType ?? 'U91', {});
    }

    return this.http
      .request(
        request.build({
          responseType: 'json',
          accept: 'application/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          const body = r.body;
          const items = body?.data ?? [];
          const validItems = items.filter(
            (item: Station) => !!item?.Brand && !!item?.Name && item.CurrentPrices.length > 0
          );

          const mapped: Array<StationDto> = validItems.map((item: Station) => {
            const lat = item?.Location?.Latitude;
            const lng = item?.Location?.Longitude;

            const location: Point =
              lat != null && lng != null
                ? {
                    type: GeometryType.Point,
                    position: {
                      latitude: lat,
                      longitude: lng,
                    },
                  }
                : null;

            const prices: Array<PriceDto> = Array.isArray(item?.CurrentPrices)
              ? item.CurrentPrices.map((p: FuelPrice) => ({
                  fuelType: p?.FuelType ?? null,
                  price: p?.Price,
                  lastUpdated: p?.LastUpdated,
                  discountedPrice: p?.MemberPrice ?? undefined,
                }))
              : [];

            const addressInfo = parseAddress(item?.Location?.Address ?? '');

            const station: StationDto = {
              name: item.Name,
              brand: item.Brand,
              code: item.Id,
              isRewardPartner: Boolean(item?.HasMemberDiscount),
              location: location,
              postcode: addressInfo.postcode || (item?.Location?.PostCode ?? null),
              prices: prices,
              stationId: item.Id,
              streetAddress: addressInfo.streetAddress,
              suburb: addressInfo.suburb,
            };
            return station;
          });

          const cloned = r.clone({ body: mapped });
          return cloned as StrictHttpResponse<Array<StationDto>>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `stationPricesGetStationsFromPoint$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  stationPricesGetStationsFromPoint(params?: {
    pointLng?: number;
    pointLat?: number;
    distanceInKm?: number;
    fuelType?: string;
  }): Observable<Array<StationDto>> {
    return this.stationPricesGetStationsFromPoint$Response(params).pipe(
      map((r: StrictHttpResponse<Array<StationDto>>) => r.body as Array<StationDto>)
    );
  }
}
